import moment from "moment";
import {
    ICustomerQuote,
    ICustomerQuoteBookingDetail,
    ICustomerQuoteProduct,
} from "@interfaces";

/**
 * Helpers for the public "Open TDMS Quote & Checkout" flow
 * (`/customerQuote/:quoteId/:randomString`).
 *
 * The backend returns the TDMS ORDER payload (`ordersProducts[]`) already
 * formatted for booking, so these helpers turn each quoted line into a
 * display-ready digest and derive the link-token/checkout state. They are
 * deliberately pure (no API/axios imports) so they unit-test without pulling the
 * ftApiClient → axios chain into jest.
 */

/** The `DD-MMM-YYYY` form the FT cart endpoints validate against (e.g. `15-May-2026`). */
const FT_API_DATE_FORMAT = "DD-MMM-YYYY";

/** Formats a quote item date is stored in across snapshots/responses. */
const PARSE_FORMATS = [moment.ISO_8601, "YYYY-MM-DD", FT_API_DATE_FORMAT];

/**
 * Normalise any stored date string to the `DD-MMM-YYYY` form the FT cart
 * endpoints require. Sending raw ISO dates triggers a generic "Validation
 * Error." from the backend, so every cart-add callsite normalises first.
 * Returns "" for unparseable input so a bad date surfaces as an obvious
 * empty value rather than moment's "Invalid date" literal.
 */
export const toFtApiDate = (date: string | null | undefined): string => {
    if (!date) return "";
    const m = moment(date, PARSE_FORMATS as moment.MomentFormatSpecification);
    return m.isValid() ? m.format(FT_API_DATE_FORMAT) : "";
};

/**
 * Total booked quantity for a quoted line. Each traveller
 * (`ordersProductsRedeemers[]`) carries a `qty`, so the line quantity is their
 * sum. Falls back to `productTotal / RRP` (then `remainingProductQuantity`, then
 * 1) for legacy/partial payloads with no redeemers. Never returns below 1.
 */
export const customerQuoteProductQuantity = (product: ICustomerQuoteProduct): number => {
    const redeemers = product.ordersProductsRedeemers || [];
    const summed = redeemers.reduce((sum, r) => sum + (Number(r?.qty) || 0), 0);
    if (summed > 0) return summed;
    const rrp = Number(product.RRP) || 0;
    const total = Number(product.productTotal) || 0;
    if (rrp > 0 && total > 0) return Math.max(1, Math.round(total / rrp));
    return Math.max(1, Number(product.remainingProductQuantity) || 1);
};

/**
 * The concrete travel date the line was quoted for, normalised to
 * `DD-MMM-YYYY`. Reads the first traveller's first booking detail; falls back to
 * the fare validity window's start for open-dated lines with no fixed date.
 */
export const customerQuoteProductDate = (product: ICustomerQuoteProduct): string => {
    const redeemers = product.ordersProductsRedeemers || [];
    for (const redeemer of redeemers) {
        for (const detail of redeemer?.ordersProductsBookingDetails || []) {
            if (detail?.travelDate) return toFtApiDate(detail.travelDate);
        }
    }
    return toFtApiDate(product.fareValidityPeriod?.travelFrom);
};

/** The names of the travellers attached to a quoted line. */
export const customerQuoteProductTravellers = (
    product: ICustomerQuoteProduct,
): string[] => {
    const names = (product.ordersProductsRedeemers || [])
        .map((r) => (r?.redeemer?.customerName || "").trim())
        .filter((name) => name.length > 0);
    return Array.from(new Set(names));
};

/** Trim a string and return `undefined` when it has no content (so it hides). */
const cleanField = (value: string | null | undefined): string | undefined => {
    const trimmed = (value ?? "").trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * A display-ready summary of one quoted line for the read-only review screen.
 * Optional fields are only populated when the source payload actually has them,
 * so the page can render each row conditionally.
 */
export interface ICustomerQuoteLine {
    productId: string;
    name: string;
    fareName?: string;
    /** Travel date in `DD-MMM-YYYY` (formatted for display at the callsite). */
    startDate: string;
    quantity: number;
    /** Per-unit RRP. */
    rrp: number;
    /** Total charged for the line (RRP × qty, or the booked total). */
    lineTotal: number;
    pickupTime?: string;
    pickupLocation?: string;
    dropoffTime?: string;
    dropoffLocation?: string;
    bookingComment?: string;
    supplierComments?: string;
    voucher?: string;
    supplierName?: string;
    supplierPhone?: string;
    supplierEmail?: string;
    travellers?: string[];
}

/** Build display digests for every quoted line. */
export const summariseCustomerQuoteItems = (
    quote: ICustomerQuote | null | undefined,
): ICustomerQuoteLine[] =>
    (quote?.ordersProducts || []).map((product) => {
        const quantity = customerQuoteProductQuantity(product);
        const rrp = Number(product.RRP) || 0;
        const lineTotal =
            Number(product.productTotal) || Number(product.totalCharged) || rrp * quantity;
        const details: ICustomerQuoteBookingDetail[] = (product.ordersProductsRedeemers || [])
            .flatMap((r) => r?.ordersProductsBookingDetails || [])
            .filter(Boolean);
        // Pick the first non-empty value for a field across ALL booking details:
        // a payload may carry a sparse first detail with the real data on a later
        // one, so we must not just read details[0].
        const firstField = (
            pick: (d: ICustomerQuoteBookingDetail) => string | null | undefined,
        ): string | undefined => {
            for (const d of details) {
                const value = cleanField(pick(d));
                if (value !== undefined) return value;
            }
            return undefined;
        };
        const travellers = customerQuoteProductTravellers(product);
        const line: ICustomerQuoteLine = {
            productId: String(product.productId),
            name: product.productName || product.altProductName || "Item",
            fareName: cleanField(product.faretypeName),
            startDate: customerQuoteProductDate(product),
            quantity,
            rrp,
            lineTotal,
            pickupTime: firstField((d) => d.pickupTime),
            pickupLocation: firstField((d) => d.pickupLocation),
            dropoffTime: firstField((d) => d.dropoffTime),
            dropoffLocation: firstField((d) => d.dropoffLocation),
            bookingComment:
                firstField((d) => d.bookingComment) ??
                firstField((d) => d.bookingDetailsComment),
            supplierComments: cleanField(product.supplierComments),
            voucher: cleanField(product.voucher),
            supplierName: cleanField(product.supplier?.supplierName),
            supplierPhone: cleanField(
                product.supplier?.workPhone || product.supplier?.reservationPhone,
            ),
            supplierEmail: cleanField(product.supplier?.infoEmail),
            travellers: travellers.length > 0 ? travellers : undefined,
        };
        return line;
    });

/**
 * Whether the customer can proceed to checkout: the quote must have at least one
 * line and the customer must have accepted the terms and conditions.
 */
export const canProceedToCheckout = (
    termsAccepted: boolean,
    itemCount: number,
): boolean => termsAccepted && itemCount > 0;

/**
 * The quote's payable total. Sums the line totals (which matches `owing` on a
 * fresh quote); falls back to the top-level `owing` / `pendingAmount` when there
 * are no line totals to sum.
 */
export const customerQuoteTotal = (quote: ICustomerQuote | null | undefined): number => {
    const products = quote?.ordersProducts || [];
    if (products.length > 0) {
        const summed = products.reduce(
            (sum, p) => sum + (Number(p.productTotal) || Number(p.totalCharged) || 0),
            0,
        );
        if (summed > 0) return summed;
    }
    return Number(quote?.owing) || Number(quote?.pendingAmount) || 0;
};

/** Decode a base64 (standard or URL-safe) string, or null when it can't be decoded. */
const decodeBase64 = (value: string | null | undefined): string | null => {
    // TDMS links use URL-safe base64 variants: `-`/`_` for `+`/`/`, and `.`
    // in place of the `=` padding (e.g. a token ending in `..` means `==`).
    // `atob` rejects `.` outright, so normalize it back to `=` first.
    const normalized = (value || "")
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .replace(/\./g, "=");
    if (!normalized) return null;
    try {
        return typeof atob === "function" ? atob(normalized) : null;
    } catch {
        return null;
    }
};

/**
 * The booking reference embedded in the public link's token. The token is the
 * base64 of `email::bookingReference`; this returns the part to the RIGHT of the
 * `::` (the booking reference), or null when it can't be extracted.
 */
export const bookingReferenceFromToken = (
    token: string | null | undefined,
): string | null => {
    const decoded = decodeBase64(token);
    if (!decoded || !decoded.includes("::")) return null;
    const ref = (decoded.split("::").pop() ?? "").trim();
    return ref.length > 0 ? ref : null;
};

/**
 * The email address embedded in the public link's token (the part to the LEFT of
 * the `::`). The backend requires this as an `?email=` query param to authorise
 * the read — without it the endpoint responds 403 "Please enter the email
 * address this quote was sent to." Returns null when it can't be extracted.
 */
export const emailFromToken = (token: string | null | undefined): string | null => {
    const decoded = decodeBase64(token);
    if (!decoded || !decoded.includes("::")) return null;
    const email = decoded.split("::")[0].trim();
    return email.length > 0 ? email : null;
};

/**
 * Whether the booking reference embedded in the link token matches the booking
 * reference of the quote the backend actually returned. Fails closed: a token
 * that can't be decoded, or a quote with no reference, does NOT match.
 */
export const quoteReferenceMatches = (
    token: string | null | undefined,
    quote: ICustomerQuote | null | undefined,
): boolean => {
    const fromToken = bookingReferenceFromToken(token);
    const fromQuote = (quote?.bookingReference ?? "").trim();
    if (!fromToken || !fromQuote) return false;
    return fromToken.toUpperCase() === fromQuote.toUpperCase();
};

export type QuoteLoadErrorKind = "not_found" | "invalid" | "error";

export interface IQuoteLoadError {
    kind: QuoteLoadErrorKind;
    message: string;
}

/**
 * Map a load failure to a user-facing reason. The public quote endpoint
 * returns 404 for an unknown quote and 401/403/410 for an invalid or expired
 * link; anything else is treated as a transient/server error.
 */
export const classifyQuoteLoadError = (err: unknown): IQuoteLoadError => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
        return {
            kind: "not_found",
            message:
                "We couldn't find this quote. The link may be incorrect or the quote no longer exists.",
        };
    }
    if (status === 401 || status === 403 || status === 410) {
        return {
            kind: "invalid",
            message: "This quote link is invalid or has expired. Please request a new link.",
        };
    }
    return {
        kind: "error",
        message: "Something went wrong loading this quote. Please try again.",
    };
};

/** Trim a comment for storage/validation. */
export const normalizeComment = (comment: string | null | undefined): string =>
    (comment ?? "").trim();

/** A comment is saveable only when it has non-whitespace content. */
export const canSaveComment = (comment: string | null | undefined): boolean =>
    normalizeComment(comment).length > 0;

/**
 * The outstanding balance for a quote. `owing` is authoritative when it is a
 * finite number — including a genuine `0` (fully settled) — so we must not let a
 * non-zero `pendingAmount` override a real zero owing. Only when `owing` is
 * missing/non-numeric do we fall back to `pendingAmount`.
 */
export const customerQuoteOwing = (quote: ICustomerQuote | null | undefined): number => {
    const owing = Number(quote?.owing);
    if (Number.isFinite(owing)) return owing;
    const pending = Number(quote?.pendingAmount);
    return Number.isFinite(pending) ? pending : 0;
};
